import { useState, useEffect } from 'react';
import { Building2, ArrowDownToLine, Clock, CheckCircle, XCircle, RefreshCw, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';

const COIN_COLORS = { BTC:'#F7931A', ETH:'#627EEA', BNB:'#F0B90B', SOL:'#9945FF', USDT:'#26A17B', SUI:'#4DA2FF', ASTER:'#FF6B00', HYPE:'#E040FB', XRP:'#0052FF' };
const COINS = ['BTC','ETH','BNB','SOL','USDT','SUI','ASTER','HYPE','XRP'];

const STATUS_CFG = {
  pending:    { color:'#F5A623', icon:Clock,        label:'Pending' },
  processing: { color:'#4DA2FF', icon:RefreshCw,    label:'Processing' },
  completed:  { color:'#00C864', icon:CheckCircle,  label:'Completed' },
  failed:     { color:'#FF3232', icon:XCircle,      label:'Failed' },
  cancelled:  { color:'#6B6B8A', icon:XCircle,      label:'Cancelled' },
};

const REGION_FLAGS = { Africa:'🌍', Americas:'🌎', Europe:'🌍', Asia:'🌏', 'Middle East':'🕌', Oceania:'🌏' };

export default function FiatWithdrawal() {
  const [tab, setTab] = useState('withdraw');
  const [grouped, setGrouped] = useState({});
  const [allCurrencies, setAllCurrencies] = useState({});
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [converting, setConverting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [openRegion, setOpenRegion] = useState('Africa');

  const [form, setForm] = useState({
    cryptoAmount: '', cryptoCurrency: 'USDT', fiatCurrency: 'NGN',
    bankDetails: { bankName:'', accountNumber:'', accountName:'', swiftCode:'', iban:'', country:'' },
  });

  useEffect(() => {
    Promise.all([
      api.get('/fiat/currencies'),
      api.get('/fiat/withdrawal/my'),
    ]).then(([c, w]) => {
      setGrouped(c.data.grouped || {});
      setAllCurrencies(c.data.currencies || {});
      setWithdrawals(w.data.withdrawals || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const getPreview = async () => {
    if (!form.cryptoAmount || !form.fiatCurrency) return;
    setConverting(true);
    try {
      const res = await api.post('/fiat/convert', { cryptoAmount: form.cryptoAmount, cryptoCurrency: form.cryptoCurrency, fiatCurrency: form.fiatCurrency });
      setPreview(res.data);
    } catch { toast.error('Conversion failed'); }
    finally { setConverting(false); }
  };

  const submitWithdrawal = async () => {
    const { bankDetails } = form;
    if (!bankDetails.bankName || !bankDetails.accountNumber || !bankDetails.accountName) return toast.error('Bank name, account number and account name required');
    if (!form.cryptoAmount || parseFloat(form.cryptoAmount) <= 0) return toast.error('Enter a valid amount');
    setSubmitting(true);
    try {
      const res = await api.post('/fiat/withdrawal/request', form);
      toast.success(res.data.message);
      setForm({ cryptoAmount:'', cryptoCurrency:'USDT', fiatCurrency:'NGN', bankDetails:{ bankName:'', accountNumber:'', accountName:'', swiftCode:'', iban:'', country:'' } });
      setPreview(null);
      setTab('history');
      const w = await api.get('/fiat/withdrawal/my');
      setWithdrawals(w.data.withdrawals || []);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const selectedFiatInfo = allCurrencies[form.fiatCurrency];

  if (loading) return <div className="flex justify-center items-center min-h-screen"><div className="spinner" /></div>;

  return (
    <div className="min-h-screen pt-20 pb-24 px-4 max-w-2xl mx-auto">
      <div className="flex items-center gap-3 mt-4 mb-6">
        <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background:'linear-gradient(135deg,#00C864,#4DA2FF)' }}>
          <Building2 size={22} className="text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily:'Sora' }}>Bank Withdrawal</h1>
          <p className="text-xs text-brand-muted">108 currencies · {Object.keys(grouped).length} regions · Live rates</p>
        </div>
      </div>

      <div className="flex gap-2 mb-6 p-1 rounded-xl" style={{ background:'#12121A', border:'1px solid #1E1E2E' }}>
        {[['withdraw','Withdraw'],['history','History']].map(([id,label]) => (
          <button key={id} onClick={() => setTab(id)}
            className="flex-1 py-2 rounded-lg text-sm font-semibold transition-all"
            style={{ background:tab===id?'linear-gradient(135deg,#FF6B00,#F5A623)':'transparent', color:tab===id?'white':'#6B6B8A' }}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'withdraw' && (
        <div className="space-y-4">
          {/* Crypto amount */}
          <div className="glass rounded-2xl p-4">
            <h2 className="font-bold mb-3 text-sm" style={{ fontFamily:'Sora' }}>Amount to Withdraw</h2>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <input type="number" className="input-dark" placeholder="Amount" min="0"
                value={form.cryptoAmount} onChange={e => { setForm({...form, cryptoAmount:e.target.value}); setPreview(null); }} />
              <select className="input-dark" value={form.cryptoCurrency}
                onChange={e => { setForm({...form, cryptoCurrency:e.target.value}); setPreview(null); }}>
                {COINS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {/* Fiat currency - grouped by region */}
          <div className="glass rounded-2xl p-4">
            <h2 className="font-bold mb-1 text-sm" style={{ fontFamily:'Sora' }}>Receive in</h2>
            <p className="text-xs text-brand-muted mb-3">108 currencies supported — select your region then currency</p>

            {/* Selected currency display */}
            {selectedFiatInfo && (
              <div className="flex items-center gap-2 p-2 rounded-xl mb-3" style={{ background:'rgba(0,200,100,0.1)', border:'1px solid rgba(0,200,100,0.3)' }}>
                <span className="text-lg">{REGION_FLAGS[selectedFiatInfo.region]}</span>
                <div>
                  <span className="font-bold text-sm" style={{ color:'#00C864' }}>{form.fiatCurrency}</span>
                  <span className="text-xs text-brand-muted ml-2">{selectedFiatInfo.name} ({selectedFiatInfo.symbol})</span>
                </div>
              </div>
            )}

            {/* Region accordions */}
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {Object.entries(grouped).map(([region, currencies]) => (
                <div key={region} className="rounded-xl overflow-hidden" style={{ border:'1px solid #1E1E2E' }}>
                  <button onClick={() => setOpenRegion(openRegion === region ? null : region)}
                    className="w-full flex items-center justify-between p-2.5 text-sm font-semibold"
                    style={{ background:'#12121A' }}>
                    <span>{REGION_FLAGS[region]} {region} ({Object.keys(currencies).length})</span>
                    <ChevronDown size={14} className={`transition-transform ${openRegion === region ? 'rotate-180' : ''}`} style={{ color:'#6B6B8A' }} />
                  </button>
                  {openRegion === region && (
                    <div className="grid grid-cols-3 gap-1 p-2" style={{ background:'#0A0A0F' }}>
                      {Object.entries(currencies).map(([code, info]) => (
                        <button key={code}
                          onClick={() => { setForm({...form, fiatCurrency:code}); setPreview(null); }}
                          className="p-1.5 rounded-lg text-center transition-all"
                          style={{
                            background: form.fiatCurrency===code ? 'rgba(0,200,100,0.15)' : '#12121A',
                            border: `1px solid ${form.fiatCurrency===code ? '#00C864' : '#1E1E2E'}`,
                          }}>
                          <p className="font-bold text-xs" style={{ color:form.fiatCurrency===code?'#00C864':'#E0E0F0', fontFamily:'JetBrains Mono' }}>{code}</p>
                          <p className="text-xs opacity-60 truncate">{info.symbol}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Preview conversion */}
          <button onClick={getPreview} disabled={converting || !form.cryptoAmount}
            className="btn-outline w-full flex items-center justify-center gap-2 disabled:opacity-40">
            {converting ? <div className="spinner" /> : <><RefreshCw size={14} /> Calculate Conversion</>}
          </button>

          {preview && (
            <div className="p-4 rounded-2xl" style={{ background:'rgba(0,200,100,0.06)', border:'1px solid rgba(0,200,100,0.25)' }}>
              <p className="text-xs text-brand-muted mb-2">Conversion Preview</p>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-bold font-mono" style={{ color:COIN_COLORS[preview.cryptoCurrency] }}>{preview.cryptoAmount} {preview.cryptoCurrency}</p>
                  <p className="text-xs text-brand-muted">= ${preview.usdValue} USD</p>
                </div>
                <div className="text-2xl px-2">→</div>
                <div className="text-right">
                  <p className="font-bold text-xl" style={{ color:'#00C864' }}>{preview.fiatSymbol} {preview.fiatAmount?.toLocaleString()}</p>
                  <p className="text-xs text-brand-muted">{preview.fiatName}</p>
                </div>
              </div>
              <p className="text-xs text-brand-muted mt-1">Rate: 1 USD = {preview.exchangeRate?.toLocaleString()} {preview.fiatCurrency}</p>
            </div>
          )}

          {/* Bank details */}
          <div className="glass rounded-2xl p-4">
            <h2 className="font-bold mb-3 text-sm" style={{ fontFamily:'Sora' }}>Bank Details</h2>
            <div className="space-y-2">
              <input className="input-dark" placeholder="Bank Name *" value={form.bankDetails.bankName}
                onChange={e => setForm({...form, bankDetails:{...form.bankDetails, bankName:e.target.value}})} />
              <input className="input-dark" placeholder="Account Number *" value={form.bankDetails.accountNumber}
                onChange={e => setForm({...form, bankDetails:{...form.bankDetails, accountNumber:e.target.value}})} />
              <input className="input-dark" placeholder="Account Name *" value={form.bankDetails.accountName}
                onChange={e => setForm({...form, bankDetails:{...form.bankDetails, accountName:e.target.value}})} />
              <input className="input-dark" placeholder="SWIFT / BIC Code (international transfers)" value={form.bankDetails.swiftCode}
                onChange={e => setForm({...form, bankDetails:{...form.bankDetails, swiftCode:e.target.value}})} />
              <input className="input-dark" placeholder="IBAN (Europe)" value={form.bankDetails.iban}
                onChange={e => setForm({...form, bankDetails:{...form.bankDetails, iban:e.target.value}})} />
              <input className="input-dark" placeholder="Country" value={form.bankDetails.country}
                onChange={e => setForm({...form, bankDetails:{...form.bankDetails, country:e.target.value}})} />
            </div>
          </div>

          <div className="p-3 rounded-xl text-xs text-brand-muted" style={{ background:'#12121A', border:'1px solid #1E1E2E' }}>
            ⏱️ Processing: 1–3 business days &nbsp;·&nbsp; Min: $10 equivalent &nbsp;·&nbsp; Rate locked at time of processing
          </div>

          <button onClick={submitWithdrawal} disabled={submitting || !preview}
            className="btn-primary w-full flex items-center justify-center gap-2 py-3 disabled:opacity-40">
            {submitting ? <div className="spinner" /> : <><ArrowDownToLine size={16} /> Submit Withdrawal Request</>}
          </button>
        </div>
      )}

      {tab === 'history' && (
        <div>
          {withdrawals.length === 0 ? (
            <div className="text-center py-20 text-brand-muted">
              <Building2 size={48} className="mx-auto mb-4 opacity-30" />
              <p>No withdrawal history yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {withdrawals.map(w => {
                const cfg = STATUS_CFG[w.status] || STATUS_CFG.pending;
                const Icon = cfg.icon;
                return (
                  <div key={w._id} className="p-4 rounded-2xl" style={{ background:'#12121A', border:'1px solid #1E1E2E' }}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-bold font-mono text-sm" style={{ color:COIN_COLORS[w.cryptoCurrency] }}>{w.amountCrypto} {w.cryptoCurrency}</p>
                        <p className="text-xs text-brand-muted">≈ ${w.amountUSD?.toFixed(2)}</p>
                      </div>
                      <div className="flex items-center gap-1 text-xs font-semibold" style={{ color:cfg.color }}>
                        <Icon size={12} /> {cfg.label}
                      </div>
                    </div>
                    <div className="flex justify-between text-xs text-brand-muted mb-1">
                      <span>{w.fiatCurrency}: {w.fiatAmount?.toLocaleString()}</span>
                      <span>{w.bankDetails?.bankName}</span>
                    </div>
                    {w.bankReference && <p className="text-xs font-mono" style={{ color:'#00C864' }}>Ref: {w.bankReference}</p>}
                    {w.failureReason && <p className="text-xs text-red-400">Reason: {w.failureReason}</p>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
